//src/pages/appointments/AppointmentsTable.jsx :-
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  styled,
  TextField,
  Typography
} from "@mui/material";
import dayjs from "dayjs";
import Refresh from "@mui/icons-material/Refresh";
import { useAuth } from "../../context/AuthContext.jsx";
import { useTheme } from "@emotion/react";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import {
  getAppointmentsByPatientId,
  generateTokenForAppointment,
  updateAppointment,
} from "../../services/appointmentService.js";
import {
  getDoctorById,
  getDoctorByDepartmentId,
} from "../../services/doctorService.js"; // Updated import for services
import { CancelOutlined } from "@mui/icons-material";
import EditAppointmentModal from "../EditAppointmentModal/index.jsx";
import AddPatientModal from "../AddPatientModal/index.tsx"; // Import the AddPatientModal
import AppointmentTabs from "../../components/AppointmentTabs"; // Import the Tabs component
import NewAdmissionModel from "../AddPatientModal/InPatientAppointmentModal.tsx"; // Import the AddPatientModal
import { getAdmissionByPatientId } from "../../services/PatientAdmissionService";
import { toast } from "react-toastify";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import {
  canStaffManualCancelRole,
  cancelAppointmentSoft,
  isAppointmentCancelled,
  isAppointmentCompleted,
  buildCancelAppointmentPayload,
  getAppointmentDisplayStatus,
  getAppointmentCancelReasonText,
  shouldShowAppointmentCancelReason,
  hasAppointmentToken,
  isAppointmentStaffActionDisabled,
  getAppointmentStaffActionDisabledReason,
  isPastAutoCancelThreshold,
  shouldShowGenerateAppointmentTokenButton,
  canGenerateAppointmentToken,
  getAppointmentTokenDisplay,
  extractTokenNumber,
  CANCEL_REASON_AUTO,
  CANCEL_REASON_MANUAL,
} from "../../utils/appointmentCancelUtils";

const columns = [
  { id: "id", label: "Appointment Id" },
  { id: "doctorName", label: "Doctor Name" },
  { id: "appointmentDate", label: "Appointment Date" },
  { id: "appointmentTime", label: "Appointment Time" },
  { id: "tokenNumber", label: "Token Number" },
  { id: "status", label: "Status" },
  { id: "cancelReason", label: "Cancel Reason" },
];

const admissionColumns = [ 
  { id: "id", label: "Admission Id" },
  { id: "patientId", label: "Patient Id" },
  { id: "admissionDate", label: "Admission Date" },
  { id: "admissionTime", label: "Admission Time" },
  { id: "status", label: "Admission Status" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

export default function AppointmentsTable({ patient, patients, onClose, isPatientAdmitted = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user } = useAuth(); // Add user from useAuth hook
  const { hasCapability, isPremiumPackage } = useSubscriptionCapabilities();
  const hasAppointmentsCapability = hasCapability("Appointments");
  const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");
  // Admissions / IN patient: Premium only
  const canUseInPatient = isPremiumPackage && hasIpdCapability;
  const canManualCancelAppointment = canStaffManualCancelRole(user?.roleName);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ appointment_list: true });
  const [appointment_list, setAppointmentList] = useState([]);
  const [appointment_info, setAppointmentInfo] = useState({});
  const [edit_appointment, setEditAppointment] = useState(false);
  const [addPatientModalOpen, setAddPatientModalOpen] = useState(false); // State for modal visibility
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [departmentName, setDepartmentName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [admissionInfo, setAdmissionInfo] = useState("");
  const [patientInfo, setPatientInfo] = useState({}); // State to hold patient info
  const [selectedTab, setSelectedTab] = useState("appointments"); // State for selected tab
  const [addAdmissionModalOpen, setAddAdmissionModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTargetAppointment, setCancelTargetAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState(CANCEL_REASON_MANUAL);

  useEffect(() => {
    if (patient && patient.id) { // Change patientId to id
      fetchAppointmentList();
      if (canUseInPatient) {
        fetchPatientAdmission();
      }

      // Use isPatientAdmitted prop to determine initial tab, fallback to patient.patientType
      // Only set to admissions if Premium IPD is available
      if (canUseInPatient && (isPatientAdmitted || patient.patientType === "IN" || patient.patient_type === "IN")) {
        setSelectedTab("admissions");
      } else {
        setSelectedTab("appointments");
      }
    } else {
      console.error("Patient is undefined or missing patientId:", patient);
    }
  }, [patient, isPatientAdmitted, canUseInPatient]);

  // Effect to handle tab switching when Premium IPD is not available
  useEffect(() => {
    // If current tab is "admissions" but Premium IPD is not available, switch to appointments
    if (!canUseInPatient && selectedTab === "admissions") {
      setSelectedTab("appointments");
    }
  }, [canUseInPatient, selectedTab]);

  // const fetchAppointmentList = async () => {
  //   // if (!patient?.patientId) {
  //   if (!patient?.id) { // Change patientId to id
  //     console.error("Patient ID is missing");
  //     return;
  //   }
  //   setFetching({ ...fetching, appointment_list: true });
  //   try {
  //     // const data = await getAppointmentsByPatientId(patient.patientId);
  //     const data = await getAppointmentsByPatientId(patient.id); // Change patientId to id
  //     setAppointmentList(data);
  //   } catch (error) {
  //     console.error("Error fetching appointments:", error);
  //   }
  //   setFetching({ ...fetching, appointment_list: false });
  // };

  const fetchAppointmentList = async () => {
    if (!patient?.id) {
      console.error("Patient ID is missing");
      return;
    }
    setFetching({ ...fetching, appointment_list: true });
    try {
      console.log(`[AppointmentsTable] Fetching appointments for patient ID: ${patient.id}`);
      const data = await getAppointmentsByPatientId(patient.id);
      
      console.log(`[AppointmentsTable] Received data:`, data);
      console.log(`[AppointmentsTable] Data type:`, typeof data);
      console.log(`[AppointmentsTable] Is array:`, Array.isArray(data));
      
      // Ensure data is an array
      const appointments = Array.isArray(data) ? data : [];
      
      console.log(`[AppointmentsTable] Processed appointments count: ${appointments.length}`);
      
      if (appointments.length === 0) {
        console.warn(`[AppointmentsTable] ⚠️ No appointments found for patient: ${patient.id}`);
        console.warn(`[AppointmentsTable] This could mean:`);
        console.warn(`  1. Backend is not filtering correctly by patientId`);
        console.warn(`  2. Backend is querying wrong table`);
        console.warn(`  3. Backend is not using organizationId correctly`);
        console.warn(`  4. No appointments exist for this patient in the organization-specific table`);
        setAppointmentList([]);
        setFetching({ ...fetching, appointment_list: false });
        return;
      }
      
      console.log(`[AppointmentsTable] ✅ Found ${appointments.length} appointments, processing...`);
      
      // Assuming the response contains the patient details in each appointment
      const appointmentsWithPatientDetails = appointments.map(appointment => {
        // Find the patient details using the patient ID
        const patientDetails = patients.find(p => p.id === appointment.patientId) || {};
        return {
          ...appointment,
          address: patientDetails.address || "", // Default to empty string if undefined
          aadhar: patientDetails.aadhar || "",
          email: patientDetails.email || "",
          dob: patientDetails.dob || "",
          mobile: patientDetails.mobile || "",
          gender: patientDetails.gender || "",
        };
      });

      // Auto-cancel past no-show appointments (no token, after appointment day)
      const now = new Date();
      const toCancel = appointmentsWithPatientDetails.filter((a) => {
        if (isAppointmentCancelled(a)) return false;
        if (isAppointmentCompleted(a)) return false;
        if (hasAppointmentToken(a)) return false;
        return isPastAutoCancelThreshold(a.appointmentDateTime, undefined, now);
      });
      if (toCancel.length > 0) {
        try {
          await Promise.all(
            toCancel.map((a) =>
              updateAppointment(a.id, buildCancelAppointmentPayload(a, CANCEL_REASON_AUTO))
            )
          );
        } catch (e) {
          console.error("Failed to auto-cancel some past appointments", e);
        }
      }

      const visibleAppointments = appointmentsWithPatientDetails.filter((a) => {
        if (isAppointmentCancelled(a)) {
          return canManualCancelAppointment;
        }

        const isPastAutoCancelDay = isPastAutoCancelThreshold(
          a.appointmentDateTime,
          undefined,
          now
        );
        if (isPastAutoCancelDay && !hasAppointmentToken(a) && !isAppointmentCompleted(a)) {
          return false;
        }
        return true;
      });

      console.log(`[AppointmentsTable] After filtering: ${visibleAppointments.length} visible appointments`);

      // Sort appointments by appointment date and time in ascending order
      visibleAppointments.sort((a, b) => {
        return dayjs(a.appointmentDateTime).diff(dayjs(b.appointmentDateTime));
      });
      console.log(`[AppointmentsTable] Setting appointment list with ${visibleAppointments.length} appointments`);
      console.log(`[AppointmentsTable] Visible appointments:`, visibleAppointments);
      setAppointmentList(visibleAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
    setFetching({ ...fetching, appointment_list: false });
  };

  const fetchDoctorDetails = async (doctorId) => {
    try {
      const doctor = await getDoctorById(doctorId);
      setDoctorDetails(doctor);
      return doctor;
    } catch (error) {
      console.error("Error fetching doctor details:", error);
    }
  };

  const fetchDepartmentName = async (departmentId) => {
    try {
      const department = await getDoctorByDepartmentId(departmentId);
      setDepartmentName(department.name);
    } catch (error) {
      console.error("Error fetching department name:", error);
    }
  };

  const fetchPatientAdmission = async () => {
    try {
      const admissions = await getAdmissionByPatientId(patient.id);
      const admitted = admissions.find(admission => admission.status === "admit");
      if (admitted) {
        setAdmissionInfo(admitted); 
      } else {
        setAdmissionInfo(null); 
      }
      return admitted;
    } catch (error) {
      console.error("Error fetching admission details:", error);
    }
  };

  const handleEditAppointment = async (appointment) => {
    setFetching({ ...fetching, doctorDetails: true });
    try {
      const doctor = await fetchDoctorDetails(appointment.doctorId);
      if (doctor?.departmentId) {
        await fetchDepartmentName(doctor.departmentId);
        setDepartmentId(doctor.departmentId);
        setDepartmentName(doctor.departmentName);
        // console.log("Department is:-", doctor.departmentName);
      } else {
        console.error("Doctor's departmentId is missing:", doctor);
      }
      setAppointmentInfo(appointment);
      setEditAppointment(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setFetching({ ...fetching, doctorDetails: false });
    }
  };

  const handleCancelAppointment = (appointment) => {
    if (isAppointmentCancelled(appointment)) {
      toast.info("This appointment is already cancelled.");
      return;
    }
    if (isAppointmentCompleted(appointment)) {
      toast.info("Cannot cancel a completed appointment.");
      return;
    }
    setCancelTargetAppointment(appointment);
    setCancelReason(CANCEL_REASON_MANUAL);
    setCancelDialogOpen(true);
  };

  const handleCancelDialogConfirm = async () => {
    const appointment = cancelTargetAppointment;
    const reason = cancelReason.trim();
    if (!reason) {
      toast.error("Cancellation reason is required.");
      return;
    }
    setCancelDialogOpen(false);
    try {
      await cancelAppointmentSoft(appointment, { reason });
      toast.success("Appointment cancelled successfully.", {
        position: "top-center",
      });
      fetchAppointmentList();
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      if (error?.code === "ALREADY_CANCELLED" || error?.code === "ALREADY_COMPLETED") {
        toast.info(error.message);
        return;
      }
      toast.error("Failed to cancel the appointment. Please try again.", {
        position: "top-center",
      });
    } finally {
      setCancelTargetAppointment(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleGenerateToken = async (appointmentId, appointmentData = null) => {
    try {
      // If appointmentData is not provided, try to find it from the appointment list
      let appointment = appointmentData;
      if (!appointment) {
        appointment = appointment_list.find(apt => apt.id === appointmentId);
      }
      
      const response = await generateTokenForAppointment(appointmentId, appointment);
      const tokenVal = extractTokenNumber(response);
      if (tokenVal != null) {
        toast.success(`Token generated successfully! Token #${tokenVal}`);
        fetchAppointmentList(); // Refresh the appointment list to show the new token
      } else {
        toast.error("Failed to generate token.");
      }
    } catch (error) {
      console.error("Error generating token:", error);
      const errorMessage = error.message || "An error occurred while generating the token.";
      toast.error(errorMessage);
    }
  };

  const handleCreateAppointment = () => 
  {
    setPatientInfo({
      id: patient.id,
      name: patient.name,
      address: patient.address || "",
      aadhar: patient.aadhar || "",
      email: patient.email || "",
      dob: patient.dob || "",
      mobile: patient.mobile || "",
      gender: patient.gender || "",
    });
    if (selectedTab === 'appointments') {
      setAddPatientModalOpen(true);     
    } else if (selectedTab === 'admissions' && canUseInPatient) {
      setAddAdmissionModalOpen(true);   
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getStatusColor = (status) => {
    const statusUpper = String(status || "").toUpperCase();
    if (statusUpper === "CONFIRMED") return theme.palette.success.main;
    if (statusUpper === "PENDING_CONFIRMATION" || statusUpper === "PENDING") {
      return theme.palette.warning.main;
    }
    if (statusUpper === "COMPLETED" || statusUpper === "CALLED") {
      return theme.palette.info.main;
    }
    if (statusUpper === "WAITING") return theme.palette.primary.main;
    if (statusUpper === "REJECTED" || statusUpper === "CANCELLED" || statusUpper === "CANCELED") {
      return theme.palette.error.main;
    }
    return theme.palette.grey[500];
  };

  const isRowActionDisabled = (row) => isAppointmentStaffActionDisabled(row);

  return (
    <Grid container>
      <Paper sx={{ width: "100%" }}>
        <AppointmentTabs
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
          patient={patient}
          patients={patients}
          hasIpdCapability={canUseInPatient}
        />
        {/* Tab Content: Appointments */}
        {selectedTab === "appointments" && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={1}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Patient Name: {patient.name}
              </Typography>
              {hasAppointmentsCapability && (
                <Button
                  variant="contained"
                  onClick={() => handleCreateAppointment(patient)}
                  sx={{ mb: 1 }}
                >
                  Create Appointment
                </Button>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 550 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <StyledTableCell
                        key={column.id}
                        align={column.align}
                        style={{ top: 57, minWidth: column.minWidth }}
                      >
                        {column.label}
                      </StyledTableCell>
                    ))}
                    <StyledTableCell key={"action"} style={{ top: 57 }}>
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    console.log(`[AppointmentsTable] Rendering - fetching: ${fetching.appointment_list}, appointment_list.length: ${appointment_list.length}`);
                    console.log(`[AppointmentsTable] Current appointment_list:`, appointment_list);
                    
                    if (fetching.appointment_list) {
                      return (
                        <TableRow>
                          <TableCell colSpan={columns.length + 1}>
                            <CircularProgress size={40} />
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    if (appointment_list.length === 0) {
                      console.log(`[AppointmentsTable] Showing 'No Appointment Record' message`);
                      return (
                        <TableRow>
                          <TableCell colSpan={columns.length + 1}>
                            <Alert>No Appointment Record</Alert>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    const paginatedAppointments = appointment_list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
                    console.log(`[AppointmentsTable] Rendering ${paginatedAppointments.length} appointments (page ${page}, rowsPerPage ${rowsPerPage})`);
                    console.log(`[AppointmentsTable] Paginated appointments:`, paginatedAppointments);
                    
                    return paginatedAppointments.map((row) => {
                      const displayStatus = getAppointmentDisplayStatus(row);
                      const rowDisabled = isRowActionDisabled(row);

                      return (
                      <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        {columns.map((column) => {
                          let value = row[column.id];
                          if (column.id === "appointmentDate") {
                            value = dayjs(row.appointmentDateTime).format("D MMM YYYY");
                          } else if (column.id === "appointmentTime") {
                            value = dayjs(row.appointmentDateTime).format("hh:mm A");
                          }
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.id === "tokenNumber" ? (
                                shouldShowGenerateAppointmentTokenButton(row) ? (
                                  <Button
                                    onClick={() => handleGenerateToken(row.id, row)}
                                    disabled={!canGenerateAppointmentToken(row)}
                                    title={
                                      canGenerateAppointmentToken(row)
                                        ? "Generate token"
                                        : "Token can be generated only on the appointment date"
                                    }
                                    sx={{
                                      color: "green",
                                      '&:hover': {
                                        color: "darkgreen",
                                      },
                                    }}
                                  >
                                    Generate Token
                                  </Button>
                                ) : (
                                  getAppointmentTokenDisplay(row) || "-"
                                )
                              ) : column.id === "status" ? (
                                <Chip
                                  label={displayStatus}
                                  size="small"
                                  sx={{
                                    backgroundColor: getStatusColor(displayStatus),
                                    color: theme.palette.common.white,
                                    fontWeight: 600,
                                  }}
                                />
                              ) : column.id === "cancelReason" ? (
                                shouldShowAppointmentCancelReason(row) ? (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.error.main, fontStyle: "italic" }}
                                  >
                                    {getAppointmentCancelReasonText(row)}
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.disabled">—</Typography>
                                )
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell>
                          <Box display="flex" flexDirection="row" gap={1}>
                            {hasAppointmentsCapability && (
                              <IconButton
                                sx={{ 
                                  color: theme.palette.primary.main,
                                  ...(rowDisabled && {
                                    color: theme.palette.grey[400],
                                    cursor: 'not-allowed',
                                  })
                                }}
                                onClick={() => {
                                  if (rowDisabled) {
                                    toast.info(
                                      getAppointmentStaffActionDisabledReason(row) ||
                                        "Cannot edit this appointment",
                                      { position: "top-center" }
                                    );
                                    return;
                                  }
                                  handleEditAppointment(row);
                                }}
                                disabled={rowDisabled}
                                title={
                                  rowDisabled
                                    ? getAppointmentStaffActionDisabledReason(row) ||
                                      "Cannot edit this appointment"
                                    : "Edit appointment"
                                }
                              >
                                <ModeEditOutlineTwoToneIcon />
                              </IconButton>
                            )}
                            {canManualCancelAppointment && !isAppointmentCancelled(row) && (
                              <IconButton
                                sx={{
                                  color: theme.palette.warning.main,
                                  ...(rowDisabled && {
                                    color: theme.palette.grey[400],
                                    cursor: "not-allowed",
                                  }),
                                }}
                                onClick={() => {
                                  if (rowDisabled) {
                                    toast.info(
                                      getAppointmentStaffActionDisabledReason(row) ||
                                        "Cannot cancel this appointment",
                                      { position: "top-center" }
                                    );
                                    return;
                                  }
                                  handleCancelAppointment(row);
                                }}
                                disabled={rowDisabled}
                                title={
                                  rowDisabled
                                    ? getAppointmentStaffActionDisabledReason(row) ||
                                      "Cannot cancel this appointment"
                                    : "Cancel appointment"
                                }
                              >
                                <CancelOutlined />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={appointment_list.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}

        {canUseInPatient && selectedTab === "admissions" && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" p={1}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                Patient Name: {patient.name?.charAt(0).toUpperCase() + patient.name?.slice(1)}
              </Typography>
              {canUseInPatient && !admissionInfo && (
                <Button
                  variant="contained"
                  onClick={() => handleCreateAppointment(patient)}
                  sx={{ mb: 1 }}
                >
                  Create Admission
                </Button>
              )}
            </Box>

            <TableContainer sx={{ maxHeight: 550 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {admissionColumns.map((column) => (
                      <StyledTableCell
                        key={column.id}
                        align={column.align}
                        style={{ top: 57, minWidth: column.minWidth }}
                      >
                        {column.label}
                      </StyledTableCell>
                    ))}
                    <StyledTableCell key="action" style={{ top: 57 }}>
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {fetching.admission_list ? (
                    <TableRow>
                      <TableCell colSpan={admissionColumns.length + 1}>
                        <CircularProgress size={40} />
                      </TableCell>
                    </TableRow>
                  ) : admissionInfo ? (
                    <TableRow hover role="checkbox" tabIndex={-1} key={admissionInfo.id}>
                      {admissionColumns.map((column) => {
                        let value = admissionInfo[column.id];

                        if (column.id === "admissionDate" && Array.isArray(admissionInfo.admissionDate)) {
                          value = dayjs(admissionInfo.admissionDate.join("-")).format("D MMM YYYY");
                        }

                        if (column.id === "admissionTime" && Array.isArray(admissionInfo.admissionTime)) {
                          value = dayjs()
                            .hour(admissionInfo.admissionTime[0])
                            .minute(admissionInfo.admissionTime[1])
                            .second(admissionInfo.admissionTime[2])
                            .format("hh:mm A");
                        }
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {value}
                          </TableCell>
                        );
                      })}

                      <TableCell>
                        {/* <Box display="flex" flexDirection="row" gap={1}>
                          <IconButton sx={{ color: theme.palette.primary.main }}>
                            <ModeEditOutlineTwoToneIcon />
                          </IconButton>
                        </Box> */}
                        <Box display="flex" flexDirection="row" gap={1}>
                          <IconButton
                            sx={{ color: theme.palette.primary.main }}
                            onClick={() => {
                              if (typeof onClose === "function") {
                                try { onClose(); } catch (e) { /* noop */ }
                              }
                              console.log("Navigating to ManagePatient with patient ID:", admissionInfo.patientId);
                              console.log("User role:", user?.roleName);
                              
                              // Navigate to the appropriate route based on user role
                              const targetRoute = user?.roleName === "frontdesk" ? "/front-desk" : "/manage-patient";
                              console.log("Target route:", targetRoute);
                              
                              navigate(targetRoute, {
                                state: { 
                                  showIpdTimeline: true,
                                  selectedPatientId: admissionInfo.patientId,
                                  selectedTab: "2" // In Patient tab
                                },
                              });
                            }}
                          >
                            <ModeEditOutlineTwoToneIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={admissionColumns.length + 1}>
                        <Alert> No Admission Record </Alert>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      { /* Modals for Appointment tab only */ }
      <AddPatientModal
        open={addPatientModalOpen}
        onClose={() => {
          setAddPatientModalOpen(false);
          fetchAppointmentList();
        }}
        patient_info={patientInfo}
      />
      <EditAppointmentModal
        open={edit_appointment}
        onClose={() => {
          setEditAppointment(false);
          fetchAppointmentList();
        }}
        appointment={appointment_info}
        doctor={doctorDetails}
        departmentName={departmentName}
        departmentId={departmentId}
        patient={patient}
      />
      {canUseInPatient && (
        <NewAdmissionModel
          open={addAdmissionModalOpen}
          onClose={async () => {
            setAddAdmissionModalOpen(false);
            await fetchAppointmentList();
            if (canUseInPatient) {
              await fetchPatientAdmission();
            }
          }}
          patient_info={patientInfo}
          refreshData={fetchPatientAdmission}
        />
      )}

      {/* Cancel appointment dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {cancelTargetAppointment?.appointmentDateTime
              ? `Appointment on ${dayjs(cancelTargetAppointment.appointmentDateTime).format("D MMM YYYY, hh:mm A")}`
              : "Are you sure you want to cancel this appointment?"}
          </Typography>
          <TextField
            label="Cancellation Reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            error={!cancelReason.trim()}
            helperText={!cancelReason.trim() ? "Reason is required" : ""}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            onClick={handleCancelDialogConfirm}
            color="error"
            variant="contained"
            disabled={!cancelReason.trim()}
          >
            Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}